
import { useEffect, useState } from 'react';

import { ENV } from '../../../config/env';

import {
  getTeamKpiCards,
  teamKpiMockData,
} from '../mocks/teamKpi.mock';

import { getTeamKpi } from '../services/teamKpiApi';

import type { TeamKpiItem } from '../types/teamKpi.types';

import {
  useManagerDate,
  formatManagerDate,
} from '../context/ManagerDateContext';

type UseTeamKpiParams = {
  ManagerEmployeeCode?: string;
};

export const useTeamKpi = ({
  ManagerEmployeeCode,
}: UseTeamKpiParams = {}) => {
  const { fromDate, toDate } = useManagerDate();

  // Format dates using local date components.
  const fromDateString = formatManagerDate(fromDate);
  const toDateString = formatManagerDate(toDate);

  const [kpiCards, setKpiCards] = useState<TeamKpiItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let cancelled = false;

    const loadKpi = async (): Promise<void> => {
      const managerEmployeeCode = String(
        ManagerEmployeeCode ?? '',
      ).trim();

      // Validate manager employee code.
      if (!managerEmployeeCode) {
        setKpiCards([]);
        setError('');
        setLoading(false);
        return;
      }

      // Validate selected dates.
      if (!fromDateString || !toDateString) {
        setKpiCards([]);
        setError('Please select a valid date range.');
        setLoading(false);
        return;
      }

      // Validate date range.
      if (fromDateString > toDateString) {
        setKpiCards([]);
        setError('From date cannot be greater than To date.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        // Use mock data when mock mode is enabled.
        if (ENV.USE_MOCK) {
          if (!cancelled) {
            setKpiCards(getTeamKpiCards(teamKpiMockData));
          }
          return;
        }

        // Prepare API payload.
        const payload = {
          ManagerEmployeeCode: managerEmployeeCode,
          fromDate: fromDateString,
          toDate: toDateString,
        };

        // Call Team KPI API.
        const response = await getTeamKpi(payload);

        // Ignore outdated requests.
        if (cancelled) {
          return;
        }

        // Map API response to KPI cards.
        setKpiCards(getTeamKpiCards(response));
      } catch (err: unknown) {
        if (cancelled) {
          return;
        }

        console.error('TEAM KPI API ERROR:', err);

        setError('Failed to load KPI cards.');
        setKpiCards([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadKpi();

    // Cancel the previous request's state updates when dependencies change.
    return () => {
      cancelled = true;
    };
  }, [
    ManagerEmployeeCode,
    fromDate,
    toDate,
    fromDateString,
    toDateString,
  ]);

  return {
    kpiCards,
    loading,
    error,
  };
};
